import React, { useState } from 'react';

/**
 * UserManagement Component
 * Handles the administration of team members.
 * Updated: Admins can now "Disable" users instead of deleting them to preserve data audit trails.
 */
const UserManagement = ({ settings, users, currentUserId, onUpdateRole, onUpdateStatus, onUpdateUser, onClose }) => {
  const [filter, setFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);

  const USER_ROLES = [
    'Admin',
    'TWG B2B User',
    'TWG Apps and Visa',
    'TWG Agent',
    'TWG Agent Master'
  ];

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(filter.toLowerCase()) || 
    u.email?.toLowerCase().includes(filter.toLowerCase())
  );

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const updateData = { 
      name: editingUser.name, 
      email: editingUser.email,
      role: editingUser.role 
    };
    
    // Only persist association if role is TWG Agent or TWG Agent Master 
    if (editingUser.role === 'TWG Agent' || editingUser.role === 'TWG Agent Master') {
      updateData.agentAssociation = editingUser.agentAssociation || '';
    } else {
      updateData.agentAssociation = null; 
    }

    onUpdateUser(editingUser.id, updateData);
    setEditingUser(null);
  };

  return (
    <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={onClose} 
            className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0464AD] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h2 className="text-3xl font-black text-slate-900 tracking-tighter leading-none">Team Members</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
              Access Control & Permissions
            </p>
          </div>
        </div>
        
        <div className="relative group">
          <svg className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0464AD] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            type="text" 
            placeholder="Filter members..." 
            className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] w-full md:w-80 shadow-sm transition-all"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">User Profile</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Permissions</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map(u => (
                  <tr key={u.id} className="border-t border-slate-50 hover:bg-slate-50/50 transition-colors group">
                    <td className="p-6">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs uppercase shadow-inner ${u.role === 'Admin' ? 'bg-[#0464AD] text-white' : 'bg-slate-100 text-slate-400'}`}>
                          {(u.name || 'U').slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-sm leading-none">
                            {u.name} {u.uid === currentUserId && <span className="text-[10px] text-[#0464AD] ml-1 font-black">(You)</span>}
                          </p>
                          <p className="text-[10px] text-slate-300 font-bold uppercase tracking-tighter mt-1">ID: {u.uid.slice(0, 8)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6">
                      <span className="text-sm font-medium text-slate-600">{u.email}</span>
                    </td>
                    <td className="p-6 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                        u.status === 'Approved' ? 'bg-[#00B7CF]/10 text-[#00B7CF] border-[#00B7CF]/20' : 
                        u.status === 'Disabled' ? 'bg-slate-100 text-slate-400 border-slate-200' :
                        'bg-[#F15E3B]/10 text-[#F15E3B] border-[#F15E3B]/20'
                      }`}>
                        {u.status || 'Pending'}
                      </span>
                    </td>
                    <td className="p-6 text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                          {u.role || 'User'}
                        </span>
                        {(u.role === 'TWG Agent' || u.role === 'TWG Agent Master') && u.agentAssociation && (
                          <span className="text-[9px] font-bold text-slate-400 uppercase italic">
                            Linked: {u.agentAssociation}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-2">
                        {u.status === 'Pending' && (
                          <button 
                            onClick={() => onUpdateStatus(u.id, 'Approved')}
                            className="p-2 text-[#00B7CF] hover:bg-[#00B7CF]/5 rounded-lg transition-colors"
                            title="Approve User"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                          </button>
                        )}
                        
                        <button 
                          onClick={() => setEditingUser(u)}
                          className="p-2 text-slate-400 hover:text-[#0464AD] hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit User"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M15.172 2.172a2.828 2.828 0 114 4L11.828 15H9v-2.828l6.172-6.172z" /></svg>
                        </button>

                        {u.uid !== currentUserId && (
                          <button 
                            onClick={() => onUpdateStatus(u.id, u.status === 'Disabled' ? 'Approved' : 'Disabled')}
                            className={`p-2 rounded-lg transition-colors ${
                              u.status === 'Disabled' ? 'text-emerald-500 hover:bg-emerald-50' : 'text-amber-500 hover:bg-amber-50'
                            }`}
                            title={u.status === 'Disabled' ? "Enable Access" : "Disable Access"}
                          >
                            {u.status === 'Disabled' ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-24 text-center">
                    <div className="flex flex-col items-center justify-center opacity-30">
                      <div className="text-5xl mb-4 text-[#0464AD]">👥</div>
                      <p className="text-sm font-medium text-slate-500">No matching team members found.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl p-8 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-black text-slate-900 tracking-tighter">Edit User</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Update profile & permissions</p>
              </div>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-[#F15E3B] transition-colors text-xl">✕</button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                <input 
                  type="text" 
                  required 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]"
                  value={editingUser.name}
                  onChange={e => setEditingUser({...editingUser, name: e.target.value})}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                <input 
                  type="email" 
                  required 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]"
                  value={editingUser.email}
                  onChange={e => setEditingUser({...editingUser, email: e.target.value})}
                />
              </div>

              <div className="h-px bg-slate-100" />

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">System Role</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                    value={editingUser.role || ''}
                    onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                    required
                    disabled={editingUser.uid === currentUserId}
                  >
                    <option value="" disabled>Select Role...</option>
                    {USER_ROLES.map(role => <option key={role} value={role}>{role}</option>)}
                  </select>
                </div>

                {/* FIXED: Wrapped in braces so it evaluates logic instead of printing text  */}
                {(editingUser.role === 'TWG Agent' || editingUser.role === 'TWG Agent Master') && (
                  <div className="space-y-1.5 animate-in slide-in-from-top-2">
                    <label className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest ml-1">Linked Agent (Source)</label>
                    <select 
                      className="w-full bg-[#0464AD]/5 border border-[#0464AD]/20 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                      value={editingUser.agentAssociation || ''}
                      onChange={(e) => setEditingUser({...editingUser, agentAssociation: e.target.value})}
                      required
                    >
                      <option value="">Select Associated Agent...</option>
                      {(settings?.sourceAgents || []).map(agent => {
                         // Support both string and object formats for source agents [cite: 13, 21]
                         const agentName = typeof agent === 'string' ? agent : agent.name;
                         return <option key={agentName} value={agentName}>{agentName}</option>;
                      })}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="flex-1 py-4 text-xs font-black text-slate-400 hover:text-slate-800 uppercase tracking-widest transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 bg-[#0464AD] text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;